import React, { useState, useEffect, useMemo } from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Helper from "../../../Helper";
import { getTrans as t } from "../../../Localization/translations";
import { API } from "../../../backend";
import authHeader from "../../../services/auth-header";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

function ContractBalances() {
  const tableRef = React.createRef();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [summaryTotals, setSummaryTotals] = useState({});


  useEffect(() => {
    loadBalanceByDeal();
  }, []);

  const loadBalanceByDeal = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(API + "accounting/balance-by-deal/", {
        headers: authHeader(),
      });

      let rowData = res.data || [];
      if (!Array.isArray(rowData)) rowData = [];

      setData(rowData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading Balance By Deal:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!data.length) return;

    const totals = {};

    data.forEach((row) => {
      Object.keys(row).forEach((key) => {
        // ❌ skip meta fields
        if (key === "contract" || key === "customer" || key === "tableData") {
          return;
        }

        const value = Number(row[key]);

        // ❌ skip non-numbers
        if (isNaN(value)) return;

        totals[key] = (totals[key] || 0) + value;
      });
    });

    // ❌ remove zero totals
    const filteredTotals = {};
    Object.entries(totals).forEach(([key, value]) => {
      if (value !== 0) {
        filteredTotals[key] = value;
      }
    });

    setSummaryTotals(filteredTotals);
  }, [data]);




  // 🔹 Dynamic columns
  const columns = useMemo(() => {
    if (!data.length) return [];

    // 🔹 Calculate totals per column
    const totals = {};

    data.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key === "contract" || key === "customer" || key === "tableData") return;

        const val = Number(row[key]);
        if (isNaN(val)) return;

        totals[key] = (totals[key] || 0) + val;
      });
    });

    // 🔹 Only keep columns whose total ≠ 0
    const subclassCols = Object.keys(totals).filter(
      (key) => totals[key] !== 0
    );

    return [
      {
        title: t("Contract"),
        field: "contract",
        headerStyle: { fontWeight: "bold", textAlign: "left" },
        cellStyle: { textAlign: "left", fontSize: 12 },
      },
      {
        title: t("Customer"),
        field: "customer",
        headerStyle: { fontWeight: "bold", textAlign: "left" },
        cellStyle: { textAlign: "left", fontSize: 12 },
      },
      ...subclassCols.map((c) => ({
        title: c,
        field: c,
        headerStyle: { fontWeight: "bold", textAlign: "left" },
        cellStyle: { textAlign: "left", fontSize: 12 },
        render: (rowData) => Helper.Amount(rowData[c] || 0),
      })),
    ];
  }, [data]);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row">
          <div className="col-md-12">

            <MaterialTable
              tableRef={tableRef}
              isLoading={isLoading}
              style={{
                backgroundColor: "var(--bs-table-bg)",
                borderBottomWidth: "1px",
                boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
              }}
              title={
                <>
                  <h5 className="mb-0">{t("Balances By Contract")}</h5>
                  <span
                    style={{
                      color: "#245C7F",
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontSize: "13px",
                    }}
                    onClick={() => setIsDialogOpen(true)}
                  >
                    {t("Summary")}
                  </span>
                </>
              }

              columns={columns}
              data={data || []}

              components={{
                Toolbar: (props) => (
                  <div className="MuiToolbar-root MTableToolbar-spacer-21 my-temp-class">
                    <MTableToolbar {...props} />
                  </div>
                ),
              }}

              options={{
                headerStyle: {
                  backgroundColor: "white",
                  color: "#245C7F",
                  fontSize: 12,
                },
                rowStyle: (rowData, index) => {
                  if (index % 2) {
                    return { backgroundColor: "#fff" };
                  }
                },
                sorting: true,
                search: true,
                toolbar: true,
                paging: true,
                pageSize: 10,
                emptyRowsWhenPaging: false,
                pageSizeOptions: [10, 20, 25, 50],
                paginationType: "stepped",
                showFirstLastPageButtons: false,

                exportAllData: true,
                exportButton: true,
                exportFileName: "BalancesByContract",

                // ✅ CSV EXPORT
                exportCsv: async (columns, data) => {
                  try {
                    const csvColumns = Helper.visibleColumn(columns);
                    const csvColumnTitles = csvColumns.map(col => col.title);

                    const csvData = data.map(row =>
                      csvColumns.map(col => row[col.field] ?? "")
                    );

                    const csvRows = [csvColumnTitles, ...csvData];
                    const csvContent = csvRows.map(r => r.join(",")).join("\n");

                    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = "BalancesByContract.csv";
                    link.click();
                  } catch (error) {
                    console.error("CSV export error:", error);
                  }
                },

                // ✅ PDF EXPORT (same pattern as your Parties page)
                exportPdf: async (columns, data) => {
                  try {
                    const visibleColumns = Helper.visibleColumn(columns);
                    if (!data || data.length === 0) return;

                    const doc = new jsPDF("l"); // landscape

                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(16);
                    doc.text("Balances By Contract", 14, 10);

                    doc.setFontSize(11);
                    doc.setFont("helvetica", "normal");
                    const currentDate = new Date().toLocaleDateString();
                    doc.text(`Date : ${currentDate}`, 14, 16);

                    const tableStartY = 22;

                    const columnTitles = visibleColumns.map(col => col.title);
                    const pdfData = data.map(row =>
                      visibleColumns.map(col => row[col.field])
                    );

                    autoTable(doc, {
                      head: [columnTitles],
                      body: pdfData,
                      startY: tableStartY,
                      styles: { fontSize: 8 },
                    });

                    doc.save("BalancesByContract.pdf");
                  } catch (error) {
                    console.error("PDF export error:", error);
                  }
                },

                actionsColumnIndex: -1,
                columnsButton: true,
              }}


              localization={{
                toolbar: { searchPlaceholder: t("Search Balances By Contract") },
              }}
            />

            <Dialog
              open={isDialogOpen}
              onClose={() => setIsDialogOpen(false)}
              PaperProps={{
                style: {
                  width: "600px",
                  maxWidth: "90%",
                  borderRadius: "8px",
                },
              }}
            >
              <DialogTitle
                style={{
                  fontWeight: "bold",
                  color: "#1E88E5",
                  borderBottom: "1px solid #E0E0E0",
                  paddingBottom: "10px",
                }}
              >
                {t("Balances By Contract Summary")}
              </DialogTitle>

              <DialogContent>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "10px",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "10px",
                            backgroundColor: "#E3F2FD",
                            color: "#1565C0",
                            fontWeight: "bold",
                            borderBottom: "2px solid #B0BEC5",
                          }}
                        >
                          Column
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "10px",
                            backgroundColor: "#E3F2FD",
                            color: "#1565C0",
                            fontWeight: "bold",
                            borderBottom: "2px solid #B0BEC5",
                          }}
                        >
                          Total
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {Object.entries(summaryTotals).map(([key, total]) => (
                        <tr key={key}>
                          <td style={{ padding: "10px", color: "#757575" }}>{key}</td>
                          <td style={{ padding: "10px", textAlign: "right", color: "#757575" }}>
                            {Helper.Amount(total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={() => setIsDialogOpen(false)}
                  color="primary"
                  style={{
                    textTransform: "none",
                    fontWeight: "bold",
                    color: "#1E88E5",
                  }}
                >
                  {t("Close")}
                </Button>
              </DialogActions>
            </Dialog>


          </div>
        </div>
      </div>
    </div>
  );
}

export default ContractBalances;
